import { useCallback, useEffect, useState, useRef } from "react";
import { MapContainer, TileLayer, Marker, Polygon, useMap, useMapEvents } from "react-leaflet";
import { divIcon } from "leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import { FeatureGroup } from "react-leaflet";
import { EditControl } from "react-leaflet-draw";
import { postApi } from "../AppApis";
import { debounce } from "lodash";
import { AiOutlineClose } from "react-icons/ai";
import { GrMapLocation } from "react-icons/gr";
import { reverseGeocode } from "../../services/geocodingService";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import "./LeafletMap.css";

// Import leaflet-draw explicitly to ensure it's available
import "leaflet-draw";

// Fix for default markers
import L from "leaflet";
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";

let DefaultIcon = L.divIcon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconAnchor: [12, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

// Custom marker creation function
const createCustomMarker = (text, count, isHovered = false) => {
    const fillColor = isHovered ? "#E67E22" : "#11907A";
    const outerColor = isHovered ? "#FAD7A0" : "#F0FDF9";
    const borderColor = isHovered ? "#E67E22" : "#11907A";

    function formatNumber(num) {
        if (num >= 1000000) {
            return (num / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
        }
        if (num >= 1000) {
            return (num / 1000).toFixed(1).replace(/\.0$/, "") + "K";
        }
        return num;
    }

    if (count) {
        // Round icon for markers with count
        const svgIcon = `
            <svg xmlns='http://www.w3.org/2000/svg' width='60' height='60' viewBox='0 0 60 60'>
                <circle cx='30' cy='30' r='22' fill='${outerColor}' stroke='${borderColor}' stroke-width='1'/>
                <circle cx='30' cy='30' r='15' fill='${fillColor}' />
                <text x='30' y='34' text-anchor='middle' font-size='12' font-family='Arial' font-weight='bold' fill='white'>${formatNumber(count)}</text>
            </svg>
        `;

        return divIcon({
            html: svgIcon,
            className: "custom-marker-cluster",
            iconSize: [60, 60],
            iconAnchor: [30, 30],
        });
    } else {
        // Rectangular icon for price markers
        const priceColor = isHovered ? "#E67E22" : "#2C3E50";
        const svgIcon = `
            <svg xmlns='http://www.w3.org/2000/svg' width='60' height='48' viewBox='0 0 31 24'>
                <rect x='0' y='0' width='31' height='15' rx='2' ry='2' fill='${priceColor}' />
                <polygon points='12.5,15 19,15 15.5,23' fill='${priceColor}' />
                <text x='15.5' y='8.5' text-anchor='middle' dominant-baseline='middle' font-size='7' font-family='Arial' font-weight='bold' fill='white'>$${formatNumber(text)}</text>
            </svg>
        `;

        return divIcon({
            html: svgIcon,
            className: "custom-marker-price",
            iconSize: [60, 48],
            iconAnchor: [30, 48],
        });
    }
};

// Component to handle map events
const MapEventsHandler = ({ onBoundsChanged, mapRef }) => {
    const map = useMap();

    useEffect(() => {
        mapRef.current = map;
    }, [map, mapRef]);

    useMapEvents({
        moveend: () => {
            onBoundsChanged();
        },
        zoomend: () => {
            onBoundsChanged();
        },
    });

    return null;
};

export default function LeafletMapComponent({
    filters,
    setAddress,
    setPropertiesCount,
    visibleRegionPayload,
    setPolygenPayload,
    setCornersPayload,
    onBoundZoom,
    mapRef,
    setCurrentPage,
    searchedArea,
    setSearchedArea,
    isFullViewMap,
    setIsFullViewMap,
    isFilterModalOpen = false,
}) {
    const [center, setCenter] = useState([44.986656, -93.258133]);
    const [zoomLevel, setZoomLevel] = useState(16);
    const [polygon, setPolygon] = useState(null);
    const [midpointsData, setMidpointsData] = useState([]);
    const [pointsData, setPointsData] = useState([]);
    const [isApplyingPolygonSearch, setIsApplyingPolygonSearch] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const featureGroupRef = useRef();

    // Track mobile state for proper rendering of custom zoom controls
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        checkMobile();
        window.addEventListener("resize", checkMobile);
        return () => window.removeEventListener("resize", checkMobile);
    }, []);

    // Handle mobile screen size changes and prevent unintended full view mode
    useEffect(() => {
        const handleResize = () => {
            // On small screens (mobile), disable full view mode to prevent UI issues
            if (window.innerWidth <= 640 && isFullViewMap) {
                setIsFullViewMap(false);
            }
        };

        // Add event listener
        window.addEventListener("resize", handleResize);

        // Check on initial load
        handleResize();

        // Cleanup
        return () => window.removeEventListener("resize", handleResize);
    }, [isFullViewMap, setIsFullViewMap]);

    // Prevent viewport scaling issues and header hiding on mobile
    useEffect(() => {
        const handleViewportChange = () => {
            // Force scroll to top when viewport changes to keep header visible
            if (window.innerWidth <= 768) {
                setTimeout(() => {
                    window.scrollTo(0, 0);
                    document.body.scrollTop = 0;
                    document.documentElement.scrollTop = 0;
                }, 100);
            }
        };

        const preventPinchZoom = (e) => {
            // Only prevent pinch-to-zoom on non-map elements to avoid browser chrome issues
            // Allow pinch-to-zoom on the map itself
            if (e.touches && e.touches.length > 1 && !e.target.closest(".leaflet-container")) {
                e.preventDefault();
                e.stopPropagation();
            }
        };

        const handleZoomControlClick = (e) => {
            // When zoom controls are clicked, prevent any scrolling behavior
            if (e.target.closest(".leaflet-control-zoom")) {
                e.preventDefault();
                e.stopPropagation();

                // Handle zoom programmatically
                const map = mapRef.current;
                if (map) {
                    if (e.target.classList.contains("leaflet-control-zoom-in") || e.target.closest(".leaflet-control-zoom-in")) {
                        map.zoomIn();
                    } else if (e.target.classList.contains("leaflet-control-zoom-out") || e.target.closest(".leaflet-control-zoom-out")) {
                        map.zoomOut();
                    }
                }

                // Force header to stay visible
                setTimeout(() => {
                    window.scrollTo(0, 0);
                    document.body.scrollTop = 0;
                    document.documentElement.scrollTop = 0;
                }, 50);
            }
        };

        // Only apply to mobile devices
        if (window.innerWidth <= 768) {
            // Listen for viewport size changes
            window.addEventListener("resize", handleViewportChange);
            window.addEventListener("orientationchange", handleViewportChange);

            // Prevent pinch-to-zoom
            document.addEventListener("touchstart", preventPinchZoom, { passive: false });
            document.addEventListener("touchmove", preventPinchZoom, { passive: false });

            // Handle zoom control clicks
            document.addEventListener("click", handleZoomControlClick, { capture: true });
            document.addEventListener("touchend", handleZoomControlClick, { capture: true });

            return () => {
                window.removeEventListener("resize", handleViewportChange);
                window.removeEventListener("orientationchange", handleViewportChange);
                document.removeEventListener("touchstart", preventPinchZoom);
                document.removeEventListener("touchmove", preventPinchZoom);
                document.removeEventListener("click", handleZoomControlClick, { capture: true });
                document.removeEventListener("touchend", handleZoomControlClick, { capture: true });
            };
        }
    }, [mapRef]);

    const showGroupedProperties = useCallback(async (corners) => {
        try {
            const response = await postApi("/properties/cluster_properties_mid_point", corners);
            if (response.response.midpoints && Array.isArray(response.response.midpoints)) {
                const validMidpoints = response.response.midpoints.filter(
                    (item) =>
                        item &&
                        typeof item.latitude === "number" &&
                        typeof item.longitude === "number" &&
                        !isNaN(item.latitude) &&
                        !isNaN(item.longitude)
                );
                setMidpointsData(validMidpoints);
            } else {
                setMidpointsData([]);
            }
        } catch (error) {
            console.error("Error loading grouped properties:", error);
            setMidpointsData([]);
        }
    }, []);

    const showIndividualProperties = useCallback(
        async (corners, isFiltersAlso) => {
            try {
                const response = await postApi("/properties/cluster_properties", corners);
                if (response.response.clusters && Array.isArray(response.response.clusters)) {
                    const validClusters = response.response.clusters.filter(
                        (item) =>
                            item &&
                            typeof item.latitude === "number" &&
                            typeof item.longitude === "number" &&
                            !isNaN(item.latitude) &&
                            !isNaN(item.longitude)
                    );

                    if (isFiltersAlso) {
                        setPointsData(validClusters);
                    } else {
                        setPointsData((prevPoints) => {
                            const mergedPoints = [...prevPoints, ...validClusters].reduce((acc, point) => {
                                if (!acc.some((p) => p.listingId === point.listingId)) {
                                    acc.push(point);
                                }
                                return acc;
                            }, []);
                            return mergedPoints;
                        });
                    }
                    setPropertiesCount(validClusters.length);
                } else {
                    setPointsData([]);
                    setPropertiesCount(0);
                }
            } catch (error) {
                console.warn("Error loading individual properties:", error);
                setPointsData([]);
                setPropertiesCount(0);
            }
        },
        [setPropertiesCount]
    );

    useEffect(() => {
        setZoomLevel(onBoundZoom);
    }, [onBoundZoom]);

    // Fix map sizing when switching to full view
    useEffect(() => {
        if (mapRef.current) {
            // Small delay to ensure DOM has updated
            setTimeout(() => {
                mapRef.current.invalidateSize();
            }, 100);
        }
    }, [isFullViewMap]);

    useEffect(() => {
        if (filters.minPrice || filters.maxPrice || filters.beds || filters.baths || filters.homeType) {
            const payload = { ...visibleRegionPayload };
            if (filters.minPrice) payload.minPrice = filters.minPrice;
            if (filters.maxPrice) payload.maxPrice = filters.maxPrice;
            if (filters.beds) payload.beds = filters.beds;
            if (filters.baths) payload.baths = filters.baths;
            if (filters.homeType) payload.type = filters.homeType;
            if (zoomLevel > 5 && zoomLevel <= 13) {
                showGroupedProperties(payload, true);
            } else if (zoomLevel > 13) {
                showIndividualProperties(payload, true);
            }
        } else {
            if (zoomLevel > 5 && zoomLevel <= 13) {
                showGroupedProperties(visibleRegionPayload);
            } else if (zoomLevel > 13) {
                showIndividualProperties(visibleRegionPayload);
            }
        }
    }, [filters, showGroupedProperties, showIndividualProperties, visibleRegionPayload, zoomLevel]);

    const handleRegionChange = useCallback(() => {
        if (polygon) return;
        if (!mapRef.current) return;
        setCurrentPage(1);

        const bounds = mapRef.current.getBounds();
        const currentZoomLevel = mapRef.current.getZoom();

        if (bounds) {
            const ne = bounds.getNorthEast();
            const sw = bounds.getSouthWest();
            const corners = {
                northeast: { latitude: ne.lat, longitude: ne.lng },
                northwest: { latitude: ne.lat, longitude: sw.lng },
                southeast: { latitude: sw.lat, longitude: ne.lng },
                southwest: { latitude: sw.lat, longitude: sw.lng },
            };

            setCornersPayload(corners);
            if (currentZoomLevel > 5 && currentZoomLevel <= 13) {
                showGroupedProperties(corners);
            } else if (currentZoomLevel > 13) {
                showIndividualProperties(corners);
            }
            setZoomLevel(currentZoomLevel);
        }
    }, [polygon, mapRef, setCurrentPage, setCornersPayload, showGroupedProperties, showIndividualProperties]);

    const onBoundsChanged = useCallback(
        debounce(() => {
            handleRegionChange();
        }, 500),
        [handleRegionChange]
    );

    const fetchPropertyDetails = async (item) => {
        try {
            // Navigate to property preview within the same app
            window.location.href = `/preview/${item.listingId || item.property_id}`;
        } catch (error) {
            console.error("Error fetching property details:", error);
        }
    };

    const handleMarkerPress = (marker) => {
        if (!marker || !marker.latitude || !marker.longitude) {
            console.warn("Invalid marker data:", marker);
            return;
        }

        if (zoomLevel < 13) {
            const newCenter = [marker.latitude, marker.longitude];
            if (mapRef.current) {
                mapRef.current.setView(newCenter, 14);
            }
        } else {
            fetchPropertyDetails(marker);
        }
    };

    useEffect(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    const { latitude, longitude } = position.coords;
                    setCenter([latitude, longitude]);

                    // Use the new reverse geocoding service
                    try {
                        const result = await reverseGeocode(latitude, longitude);
                        if (result && result.display_name) {
                            setAddress(result.display_name);
                        }
                    } catch (error) {
                        console.error("Error fetching address:", error);
                    }
                },
                (error) => {
                    console.log("Error fetching location: ", error);
                }
            );
        }

        // Cleanup function
        return () => {
            if (featureGroupRef.current) {
                featureGroupRef.current.clearLayers();
            }
            // Clear marker data to prevent memory leaks
            setMidpointsData([]);
            setPointsData([]);
        };
    }, [setAddress]);

    const clearPolygon = () => {
        if (featureGroupRef.current) {
            featureGroupRef.current.clearLayers();
        }
        setPolygon(null);
        setPolygenPayload(null);
        setSearchedArea(null);

        // Refresh properties for current viewport
        handleRegionChange();
    };

    const onPolygonCreated = async (e) => {
        const { layer } = e;
        if (!layer || !layer.getLatLngs) {
            console.error("Invalid layer object:", layer);
            return;
        }

        const coordinates = layer.getLatLngs()[0];
        if (!coordinates || coordinates.length === 0) {
            console.error("No coordinates found in polygon");
            return;
        }

        // Create bounding box from polygon
        let minLat = Infinity,
            maxLat = -Infinity;
        let minLng = Infinity,
            maxLng = -Infinity;

        coordinates.forEach((coord) => {
            minLat = Math.min(minLat, coord.lat);
            maxLat = Math.max(maxLat, coord.lat);
            minLng = Math.min(minLng, coord.lng);
            maxLng = Math.max(maxLng, coord.lng);
        });

        const cornersPayload = {
            northeast: { latitude: maxLat, longitude: maxLng },
            southwest: { latitude: minLat, longitude: minLng },
            northwest: { latitude: maxLat, longitude: minLng },
            southeast: { latitude: minLat, longitude: maxLng },
        };

        // Set the polygon data
        setPolygenPayload(cornersPayload);
        setPolygon(layer);

        // Automatically apply the search when polygon is completed
        setCurrentPage(1);
        setIsApplyingPolygonSearch(true);

        const currentZoomLevel = mapRef.current?.getZoom() || zoomLevel;

        try {
            if (currentZoomLevel > 5 && currentZoomLevel <= 13) {
                await showGroupedProperties(cornersPayload);
            } else if (currentZoomLevel > 13) {
                await showIndividualProperties(cornersPayload, true);
            }

            // Add a small delay to show the loading state
            setTimeout(() => {
                setIsApplyingPolygonSearch(false);
            }, 500);
        } catch (error) {
            console.error("Error applying polygon search:", error);
            setIsApplyingPolygonSearch(false);
        }
    };

    // Create polygon coordinates for searchedArea
    let coordinates = [];
    if (searchedArea?.sw && searchedArea?.nw && searchedArea?.ne && searchedArea?.se) {
        coordinates = [
            [searchedArea.sw.lat, searchedArea.sw.lng],
            [searchedArea.nw.lat, searchedArea.nw.lng],
            [searchedArea.ne.lat, searchedArea.ne.lng],
            [searchedArea.se.lat, searchedArea.se.lng],
        ];
    }

    const isBoundaryDrawed = searchedArea?.sw || polygon;

    return (
        <div className="tw-h-full tw-relative">
            {isBoundaryDrawed && !isFilterModalOpen && (
                <button
                    onClick={clearPolygon}
                    className="tw-z-[99] tw-absolute tw-left-14 tw-top-3 tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-bg-white tw-rounded-lg tw-shadow-md tw-border-0 tw-text-[13px] tw-font-medium tw-text-gray-700 hover:tw-bg-gray-50 hover:tw-shadow-lg tw-transition-all tw-duration-150"
                    style={{ fontFamily: "Roboto, Arial, sans-serif" }}
                >
                    <AiOutlineClose className="tw-text-[14px]" />
                    Clear area
                </button>
            )}

            {isApplyingPolygonSearch && (
                <div className="tw-z-[100] tw-absolute tw-left-1/2 tw-top-1/2 tw-transform tw--translate-x-1/2 tw--translate-y-1/2 tw-bg-white tw-rounded-lg tw-shadow-lg tw-px-6 tw-py-4 tw-flex tw-items-center tw-gap-3">
                    <div className="tw-w-5 tw-h-5 tw-border-2 tw-border-blue-500 tw-border-t-transparent tw-rounded-full tw-animate-spin"></div>
                    <span className="tw-text-[14px] tw-font-medium tw-text-gray-700" style={{ fontFamily: "Roboto, Arial, sans-serif" }}>
                        Searching properties in area...
                    </span>
                </div>
            )}

            {/* Custom Mobile Zoom Controls - Only rendered on mobile, hidden when filter modal is open */}
            {isMobile && !isFilterModalOpen && (
                <div className="tw-absolute tw-left-3 tw-top-20 tw-z-[99] tw-flex tw-flex-col tw-bg-white tw-rounded-lg tw-shadow-md tw-overflow-hidden">
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (mapRef.current) {
                                mapRef.current.zoomIn();
                            }
                            // Force header to stay visible
                            setTimeout(() => {
                                window.scrollTo(0, 0);
                            }, 50);
                        }}
                        className="tw-w-10 tw-h-10 tw-flex tw-items-center tw-justify-center tw-bg-white tw-border-0 tw-text-[18px] tw-font-bold tw-text-gray-700 hover:tw-bg-gray-50 tw-transition-all tw-duration-150 tw-border-b tw-border-gray-200"
                        style={{ fontFamily: "Arial, sans-serif", touchAction: "manipulation" }}
                    >
                        +
                    </button>
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (mapRef.current) {
                                mapRef.current.zoomOut();
                            }
                            // Force header to stay visible
                            setTimeout(() => {
                                window.scrollTo(0, 0);
                            }, 50);
                        }}
                        className="tw-w-10 tw-h-10 tw-flex tw-items-center tw-justify-center tw-bg-white tw-border-0 tw-text-[18px] tw-font-bold tw-text-gray-700 hover:tw-bg-gray-50 tw-transition-all tw-duration-150"
                        style={{ fontFamily: "Arial, sans-serif", touchAction: "manipulation" }}
                    >
                        −
                    </button>
                </div>
            )}

            {/* View Full Map Toggle Button - Hidden when filter modal is open */}
            {!isFilterModalOpen && (
            <button
                onClick={() => {
                    // On mobile screens, only allow exiting full view mode, not entering it
                    if (window.innerWidth <= 640 && !isFullViewMap) {
                        return; // Prevent entering full view mode on mobile
                    }
                    setIsFullViewMap(!isFullViewMap);
                }}
                className={`tw-absolute tw-right-10 tw-top-3 tw-z-[99] tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-bg-white tw-rounded-lg tw-shadow-md tw-border-0 tw-text-[13px] tw-font-medium tw-text-gray-700 hover:tw-bg-gray-50 hover:tw-shadow-lg tw-transition-all tw-duration-150 ${isFullViewMap ? "tw-block" : "max-sm:tw-hidden"}`}
                style={{ fontFamily: "Roboto, Arial, sans-serif" }}
            >
                {isFullViewMap ? (
                    <>
                        <AiOutlineClose className="tw-text-[14px]" />
                        Exit Full Map
                    </>
                ) : (
                    <>
                        <GrMapLocation className="tw-text-[14px]" />
                        View Full Map
                    </>
                )}
            </button>
            )}

            <MapContainer
                center={center}
                zoom={zoomLevel}
                style={{ height: "100%", width: "100%" }}
                zoomControl={!isMobile}
                scrollWheelZoom={true}
                touchZoom={true}
                doubleClickZoom={true}
            >
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />

                <MapEventsHandler onBoundsChanged={onBoundsChanged} mapRef={mapRef} />

                {/* Searched Area Polygon */}
                {coordinates.length > 0 && (
                    <Polygon
                        positions={coordinates}
                        pathOptions={{
                            color: "#4285f4",
                            weight: 2,
                            fillColor: "#4285f4",
                            fillOpacity: 0.15,
                            opacity: 1,
                        }}
                    />
                )}

                {/* Drawing Controls */}
                <FeatureGroup ref={featureGroupRef}>
                    {(() => {
                        try {
                            return (
                                <EditControl
                                    position="topleft"
                                    onCreated={onPolygonCreated}
                                    onDeleted={() => {
                                        setPolygon(null);
                                        setPolygenPayload(null);
                                        setSearchedArea(null);

                                        // Refresh properties for current viewport
                                        handleRegionChange();
                                    }}
                                    draw={{
                                        rectangle: false,
                                        circle: false,
                                        circlemarker: false,
                                        marker: false,
                                        polyline: false,
                                        polygon: {
                                            allowIntersection: false,
                                            drawError: {
                                                color: "#ea4335",
                                                message: "<strong>Invalid shape!</strong> Overlapping areas are not allowed.",
                                                timeout: 2500,
                                            },
                                            shapeOptions: {
                                                color: "#4285f4",
                                                fillColor: "#4285f4",
                                                fillOpacity: 0.15,
                                                weight: 2,
                                                opacity: 1,
                                                clickable: true,
                                                stroke: true,
                                            },
                                            showArea: true,
                                            metric: true,
                                            feet: false,
                                            guidelineDistance: 20,
                                            tooltip: {
                                                start: "Click to start drawing area",
                                                cont: "Click to continue drawing area",
                                                end: "Click first point to close area and search",
                                            },
                                        },
                                    }}
                                    edit={{
                                        edit: false,
                                        remove: false,
                                    }}
                                />
                            );
                        } catch (error) {
                            console.error("Error rendering EditControl:", error);
                            return null;
                        }
                    })()}
                </FeatureGroup>

                {/* Property Markers */}
                {zoomLevel < 13 ? (
                    // Grouped markers for lower zoom levels
                    midpointsData.length > 0 &&
                    midpointsData
                        .filter(
                            (item) =>
                                item &&
                                typeof item.latitude === "number" &&
                                typeof item.longitude === "number" &&
                                !isNaN(item.latitude) &&
                                !isNaN(item.longitude) &&
                                item.latitude >= -90 &&
                                item.latitude <= 90 &&
                                item.longitude >= -180 &&
                                item.longitude <= 180
                        )
                        .map((item, index) => (
                            <Marker
                                key={`midpoint-${item.latitude}-${item.longitude}-${index}`}
                                position={[item.latitude, item.longitude]}
                                icon={createCustomMarker(null, item.count)}
                                eventHandlers={{
                                    click: () => item && handleMarkerPress(item),
                                }}
                            />
                        ))
                ) : (
                    // Individual markers with clustering for higher zoom levels
                    <MarkerClusterGroup
                        chunkedLoading
                        spiderfyOnMaxZoom={false}
                        showCoverageOnHover={false}
                        zoomToBoundsOnClick={true}
                        maxClusterRadius={50}
                    >
                        {pointsData
                            .filter(
                                (marker) =>
                                    marker &&
                                    typeof marker.latitude === "number" &&
                                    typeof marker.longitude === "number" &&
                                    !isNaN(marker.latitude) &&
                                    !isNaN(marker.longitude) &&
                                    marker.latitude >= -90 &&
                                    marker.latitude <= 90 &&
                                    marker.longitude >= -180 &&
                                    marker.longitude <= 180
                            )
                            .map((marker, index) => (
                                <Marker
                                    key={`point-${marker.listingId || marker.id || marker.latitude}-${marker.longitude}-${index}`}
                                    position={[marker.latitude, marker.longitude]}
                                    icon={createCustomMarker(marker.price)}
                                    eventHandlers={{
                                        click: () => marker && handleMarkerPress(marker),
                                    }}
                                />
                            ))}
                    </MarkerClusterGroup>
                )}
            </MapContainer>
        </div>
    );
}
